/**
 * EdgeDetailSheet component with Context
 * エッジ詳細を表示するドロワーコンポーネント
 */

import React, { useCallback, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet';
import { 
  Card, 
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { 
  AlertCircleIcon, 
  ClockIcon, 
  InfoIcon, 
  LinkIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { getPhaseColor, getPhaseName } from '@/types/phaseTypes';
import { TopicEdge, getEdgeStatusColor, getStatusVariant, getStatusLabel } from '@/types/edgeTypes';
import { useMapState, useMapExtraction, useMapUI } from '@/contexts/map';

/**
 * Spinner component
 */
const Spinner = ({ className = 'h-4 w-4' }: { className?: string }) => (
  <div className={`animate-spin rounded-full border-b-2 border-foreground ${className}`} />
);

/**
 * EdgeDetailSheet props
 */
interface EdgeDetailSheetProps {
  edge: TopicEdge | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * EdgeDetailSheet component
 */
const EdgeDetailSheet: React.FC<EdgeDetailSheetProps> = ({
  edge,
  isOpen,
  onClose,
}) => {
  const { mapData, updateEdge } = useMapState();
  const { retryEdge } = useMapExtraction();
  const { selectNode } = useMapUI();
  const [activeTab, setActiveTab] = useState('timeline');
  
  // Hooksはコンポーネントのトップレベルで定義する必要があります
  // これらのfunctionは条件付きで実行されないように定義しています
  const handleRetry = useCallback(async () => {
    if (!edge || !mapData) return;
    
    try {
      const updatedEdge = await retryEdge(edge.id, mapData.nodes, mapData.edges, mapData.transcript);
      if (updatedEdge) {
        updateEdge(updatedEdge.id, updatedEdge);
      }
    } catch (error) {
      console.error('Failed to retry edge extraction:', error);
    }
  }, [edge, mapData, retryEdge, updateEdge]);
  
  const handleNodeSelect = useCallback((nodeId: string) => {
    onClose();
    selectNode(nodeId);
  }, [onClose, selectNode]);
  
  // edgeがnullの場合は何も表示しない
  if (!edge || !mapData) return null;
  
  // ソースノードとターゲットノードを取得
  const sourceNode = mapData.nodes.find(node => node.id === edge.source);
  const targetNode = mapData.nodes.find(node => node.id === edge.target);
  
  // タイムスタンプでソートされたコンテンツ
  const sortedContents = [...(edge.contents || [])].sort((a, b) => {
    return a.timestamp.localeCompare(b.timestamp);
  });
  
  const statusVariant = getStatusVariant(edge.status);
  const statusLabel = getStatusLabel(edge.status);
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-full max-w-md sm:max-w-lg p-0 flex flex-col">
        <div className="flex-1 overflow-hidden flex flex-col">
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-start justify-between mb-1.5">
              <Badge variant="outline" className="px-2 py-1">
                関連性
              </Badge>
              <Badge variant={statusVariant} className="ml-2 px-2 h-6 flex items-center gap-1">
                {edge.status === 'processing' && <Spinner className="h-3 w-3 mr-1" />}
                {edge.status === 'error' && <AlertCircleIcon className="h-3 w-3 mr-1" />}
                {statusLabel}
              </Badge>
            </div>
            <SheetTitle className="text-xl">
              {edge.question}
            </SheetTitle>
            <div className="flex items-center justify-between pt-1 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="font-medium">強度:</span>
                <Badge variant="secondary">
                  {edge.strength === 'strong' ? '強' : edge.strength === 'medium' ? '中' : '弱'}
                </Badge>
              </div>
              {edge.status === 'error' && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry} 
                  className="h-7 gap-1"
                >
                  <RefreshCwIcon className="h-3 w-3" />
                  再試行
                </Button>
              )}
            </div>
          </SheetHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden p-4">
            <TabsList className="mb-0 mt-0">
              <TabsTrigger value="timeline" className="flex items-center">
                <ClockIcon className="h-4 w-4 mr-2" />
                タイムライン
              </TabsTrigger>
              <TabsTrigger value="details" className="flex items-center">
                <InfoIcon className="h-4 w-4 mr-2" />
                詳細
              </TabsTrigger>
            </TabsList>
            
            <ScrollArea className="flex-1">
              <TabsContent value="timeline" className="mt-0">
                {/* 会話文脈のタイムライン表示 */}
                <h3 className="text-sm font-medium mb-3">会話の文脈と流れ</h3>
                
                {edge.status === 'processing' ? (
                  <div className="py-8 flex flex-col items-center justify-center text-muted-foreground">
                    <Spinner className="h-6 w-6 mb-2" />
                    <span>処理中...</span>
                  </div>
                ) : edge.status === 'error' ? (
                  <Alert variant="destructive" className="mb-2">
                    <AlertCircleIcon className="h-4 w-4" />
                    <AlertTitle>エラーが発生しました</AlertTitle>
                    <AlertDescription>
                      {edge.error?.message || "不明なエラーです"}
                    </AlertDescription>
                  </Alert>
                ) : !edge.active ? (
                  <div className="py-8 flex flex-col items-center justify-center text-muted-foreground">
                    <p className="text-sm italic">関連性が見つかりませんでした</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* ソースノードの内容 */}
                    <div className="pb-4 pt-1 relative">
                      <div className="absolute left-6 top-0 h-full border-l-2 border-dashed border-muted"></div>
                      <div className="flex items-center mb-2 relative z-10">
                        <div className="w-12 h-12 rounded-full bg-background flex items-center justify-center border-2" style={{ borderColor: getEdgeStatusColor(edge.status) }}>
                          <div className="w-6 h-6 rounded-full" style={{ backgroundColor: getPhaseColor(sourceNode?.phaseId || '') }}></div>
                        </div>
                        <div className="ml-3">
                          <div 
                            className="font-medium cursor-pointer hover:underline"
                            onClick={() => sourceNode && handleNodeSelect(sourceNode.id)}
                          >
                            {sourceNode?.title || "不明なノード"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {getPhaseName(sourceNode?.phaseId || '')}
                          </div>
                        </div>
                      </div>
                      
                      {/* ソースノードのコンテンツ */}
                      {sourceNode?.contents && sourceNode.contents.length > 0 ? (
                        <div className="ml-16 border-l-4 pl-4 py-2" style={{ borderColor: getPhaseColor(sourceNode.phaseId) }}>
                          <div className="text-sm mb-1.5">
                            {sourceNode.contents[0].content}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center">
                            <ClockIcon className="h-3.5 w-3.5 mr-1.5" />
                            {sourceNode.contents[0].timestamp}
                          </div>
                          {sourceNode.contents.length > 1 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              他 {sourceNode.contents.length - 1} 件の内容あり
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="ml-16 text-sm text-muted-foreground italic">
                          内容がありません
                        </div>
                      )}
                    </div>
                    
                    {/* エッジの質問 */}
                    <div className="ml-6 pl-10 relative">
                      <div className="absolute left-0 top-0 h-full border-l-2 border-dashed border-muted"></div>
                      <div className="bg-muted/30 rounded-md p-3 relative">
                        <div className="absolute left-0 top-1/2 w-4 h-0.5 -ml-4 bg-muted"></div>
                        <div className="text-sm font-medium text-primary-foreground/80 italic">
                          {edge.question}
                        </div>
                      </div>
                    </div>
                    
                    {/* エッジのコンテンツ */}
                    {sortedContents.length > 0 ? (
                      <div className="ml-6 pl-10 relative">
                        <div className="absolute left-0 top-0 h-full border-l-2 border-dashed border-muted"></div>
                        <div className="space-y-3">
                          {sortedContents.map((content, index) => (
                            <div key={index} className="border rounded-md p-3 space-y-2 bg-primary/5">
                              <div className="text-sm">{content.content}</div>
                              <div className="text-xs text-muted-foreground flex items-center">
                                <ClockIcon className="h-3.5 w-3.5 mr-1.5" />
                                {content.timestamp}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    
                    {/* ターゲットノードの内容 */}
                    <div className="pt-4 relative">
                      <div className="absolute left-6 top-0 h-full border-l-2 border-dashed border-muted"></div>
                      <div className="flex items-center mb-2 relative z-10">
                        <div className="w-12 h-12 rounded-full bg-background flex items-center justify-center border-2" style={{ borderColor: getEdgeStatusColor(edge.status) }}>
                          <div className="w-6 h-6 rounded-full" style={{ backgroundColor: getPhaseColor(targetNode?.phaseId || '') }}></div>
                        </div>
                        <div className="ml-3">
                          <div 
                            className="font-medium cursor-pointer hover:underline"
                            onClick={() => targetNode && handleNodeSelect(targetNode.id)}
                          >
                            {targetNode?.title || "不明なノード"}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {getPhaseName(targetNode?.phaseId || '')}
                          </div>
                        </div>
                      </div>
                      
                      {/* ターゲットノードのコンテンツ */}
                      {targetNode?.contents && targetNode.contents.length > 0 ? (
                        <div className="ml-16 border-l-4 pl-4 py-2" style={{ borderColor: getPhaseColor(targetNode.phaseId) }}>
                          <div className="text-sm mb-1.5">
                            {targetNode.contents[0].content}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center">
                            <ClockIcon className="h-3.5 w-3.5 mr-1.5" />
                            {targetNode.contents[0].timestamp}
                          </div>
                          {targetNode.contents.length > 1 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              他 {targetNode.contents.length - 1} 件の内容あり
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="ml-16 text-sm text-muted-foreground italic">
                          内容がありません
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="details" className="mt-0 p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-medium mb-2">抽出プロンプト</h3>
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-sm">{edge.extractionPrompt}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-medium mb-2">メタデータ</h3>
                    <Card>
                      <CardContent className="p-4">
                        <div className="grid grid-cols-[120px_1fr] gap-y-2 text-sm">
                          <span className="text-muted-foreground">ID</span>
                          <span className="font-mono text-xs truncate">{edge.id}</span>
                          <span className="text-muted-foreground">ソースノード</span>
                          <span className="font-mono text-xs">{edge.source}</span>
                          <span className="text-muted-foreground">ターゲットノード</span>
                          <span className="font-mono text-xs">{edge.target}</span>
                          <span className="text-muted-foreground">強度</span>
                          <span>{edge.strength}</span>
                          <span className="text-muted-foreground">ステータス</span>
                          <div className="flex items-center">
                            <Badge variant={statusVariant} className="text-xs">
                              {statusLabel}
                            </Badge>
                          </div>
                          <span className="text-muted-foreground">アクティブ</span>
                          <span>{edge.active ? 'はい' : 'いいえ'}</span>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>
            </ScrollArea>
          </Tabs>
        </div>
        
        <SheetFooter className="px-6 py-4 border-t">
          <Button variant="outline" onClick={onClose}>
            閉じる
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default EdgeDetailSheet;